import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  IconSuccess, IconWarning, IconLocation, IconInfo, IconDeclined,
} from '@americanexpress/dls-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
} from '@americanexpress/dls-react';
import { parseHtml, getClassNameWithoutHorizontalPadding } from '../utils/util';
import VerticalMultiTracker from './Element/VerticalMultiTracker';
import P from './Element/Paragraph';
import ShimmerLoader from './Element/ShimmerLoader';
import ErrorBoundary from './Element/ErrorBoundary';

// Core components - eagerly loaded (used in most forms)
import Input from './Element/Input';
import DlsInput from './Element/DlsInput';
import DlsSelect from './Element/DlsSelect';
import Select from './Element/Select';
import Button from './Element/Button';
import Checkbox from './Element/Checkbox';
import RadioButton from './Element/RadioButton';
import TextArea from './Element/TextArea';
import HeadingComponent from './Element/HeadingComponent';
import ContinuousLinearTrackerProgress from './Element/ContinuousLinearTracker';
import Navigation from './Element/Navigation';
import ProgressCircularDls from './Element/ProgressCircularDls';
import DlsNotification from './Element/DlsNotification';
import FormSummarySection from './Element/FormSummarySection';

// Lazy-loaded components - loaded on demand
/* eslint-disable import/no-cycle -- need */
const Password = React.lazy(() => import(/* webpackChunkName: "password" */ './Element/Password'));
const Currency = React.lazy(() => import(/* webpackChunkName: "currency" */ './Element/CurrencyInput'));
const DlsAccordion = React.lazy(() => import(/* webpackChunkName: "dlsAccordion" */ './Element/DlsAccordion'));
const Modal = React.lazy(() => import(/* webpackChunkName: "modal" */ './Element/Modal'));
const Image = React.lazy(() => import(/* webpackChunkName: "image" */ './Element/Image'));
const Camera = React.lazy(() => import(/* webpackChunkName: "camera" */ './Camera'));
const CameraVideo = React.lazy(() => import(/* webpackChunkName: "cameraVideo" */ './CameraVideo'));
const FileUpload = React.lazy(() => import(/* webpackChunkName: "fileUpload" */ './Element/FileUpload'));
const DateSelector = React.lazy(() => import(/* webpackChunkName: "dateSelector" */ './Element/DateSelector'));
const InputDate = React.lazy(() => import(/* webpackChunkName: "inputDate" */ './Element/InputDate'));
const QrCode = React.lazy(() => import(/* webpackChunkName: "qrCode" */ './Element/Qrcode'));
const QrWrapper = React.lazy(() => import(/* webpackChunkName: "qrWrapper" */ './Element/QrWrapper'));
const Consent = React.lazy(() => import(/* webpackChunkName: "consent" */ './Element/consent'));
const RequestLimit = React.lazy(() => import(/* webpackChunkName: "requestLimit" */ './Element/RequestLimit'));
const CardSelection = React.lazy(() => import(/* webpackChunkName: "cardSelection" */ './Element/CardSelection'));
const MultiTracker = React.lazy(() => import(/* webpackChunkName: "multiTracker" */ './Element/MultiTracker'));
const HeroBanner = React.lazy(() => import(/* webpackChunkName: "heroBanner" */ './Element/HeroBanner'));
const Esign = React.lazy(() => import(/* webpackChunkName: "esign" */ './Element/Esign'));
const Singpass = React.lazy(() => import(/* webpackChunkName: "singpass" */ './Element/Singpass'));
const CardPayment = React.lazy(() => import(/* webpackChunkName: "cardPayment" */ './Element/CardPayment'));
const MultiSelectComponent = React.lazy(() => import(/* webpackChunkName: "multiSelect" */ './Element/MultiSelectComponent'));
const IconWithLabel = React.lazy(() => import(/* webpackChunkName: "iconWithLabel" */ './Element/IconWithLabel'));
const StaticTable = React.lazy(() => import(/* webpackChunkName: "staticTable" */ './Element/StaticTable'));
const CardDetails = React.lazy(() => import(/* webpackChunkName: "cardDetails" */ './Element/CardDetails'));
const ShowSubKeys = React.lazy(() => import(/* webpackChunkName: "showSubKeys" */ './Element/ShowSubmissionKeysVal'));
const PayloadFileAttachment = React.lazy(() => import(/* webpackChunkName: "payloadFileAttachment" */ './Element/PayloadFileAttachment'));
const PayloadFileAttachmentV2 = React.lazy(() => import(/* webpackChunkName: "payloadFileAttachmentV2" */ './Element/PayloadFileAttachmentV2'));
const ReCaptcha = React.lazy(() => import(/* webpackChunkName: "reCaptcha" */ './Element/ReCaptcha'));
const ApiButton = React.lazy(() => import(/* webpackChunkName: "apiButton" */ './Element/ApiButton'));
const ExpandableTable = React.lazy(() => import(/* webpackChunkName: "expandableTable" */ './Element/ExpandableTable'));
const CopyLink = React.lazy(() => import(/* webpackChunkName: "copyLink" */ './Element/CopyLink'));
const GroupCheckboxesMultiSelect = React.lazy(() => import(/* webpackChunkName: "groupCheckboxesMultiSelect" */ './Element/GroupCheckBoxMultiSelect'));
const CreateQr = React.lazy(() => import(/* webpackChunkName: "createQr" */ './Element/CreateQr'));
const GroupedCheckboxes = React.lazy(() => import(/* webpackChunkName: "groupedCheckboxes" */ './Element/GroupedCheckboxes'));
const PopUp = React.lazy(() => import(/* webpackChunkName: "popUp" */ './Element/PopUp'));
const Search = React.lazy(() => import(/* webpackChunkName: "search" */ './Element/Search'));
const Maps = React.lazy(() => import(/* webpackChunkName: "maps" */ './Element/Maps'));
const Anchor = React.lazy(() => import(/* webpackChunkName: "anchor" */ './Element/Anchor'));
const Phone = React.lazy(() => import(/* webpackChunkName: "phone" */ './Element/PhoneInput'));
const DynamicDlsAccordion = React.lazy(() => import(/* webpackChunkName: "dynamicDlsAccordion" */ './Element/DynamicDlsAccordion'));
const SuppCardComponent = React.lazy(() => import(/* webpackChunkName: "suppCard" */ './Element/SuppCardComponent'));
const SuppCardDetails = React.lazy(() => import(/* webpackChunkName: "suppCardDetails" */ './Element/SuppCardDetails'));
const LeMappings = React.lazy(() => import(/* webpackChunkName: "leMappings" */ './Element/LeMapping'));
const SplitInput = React.lazy(() => import(/* webpackChunkName: "splitInput" */ './Element/SplitInput'));
const GroupedImageSelect = React.lazy(() => import(/* webpackChunkName: "groupedImageSelect" */ './Element/GroupedImageSelect'));
const ActionableCard = React.lazy(() => import(/* webpackChunkName: "actionableCard" */ './Element/ActionableCard'));
const LeReconDoc = React.lazy(() => import(/* webpackChunkName: "leReconDoc" */ './Element/LeReconDoc'));
const DlsCarousel = React.lazy(() => import(/* webpackChunkName: "dlsCarousel" */ './Element/DlsCarousel'));
const DlsTab = React.lazy(() => import(/* webpackChunkName: "dlsTab" */ './Element/DlsTab'));
const DlsDateRangePicker = React.lazy(() => import(/* webpackChunkName: "dateRangePicker" */ './Element/DateRangePicker'));
const List = React.lazy(() => import(/* webpackChunkName: "list" */ './Element/List'));

const LazyWrap = ({
  children, height = '40px', width = '100%', className,
}) => (
  <ErrorBoundary>
    <Suspense fallback={<ShimmerLoader height={height} width={width} className={className} />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

LazyWrap.propTypes = {
  children: PropTypes.node.isRequired,
  height: PropTypes.string,
  width: PropTypes.string,
  className: PropTypes.string,
};

const findElement = (childProps, props) => {
  // props are static props here
  const {
    refreshForm,
    formSubmissionPayload,
    selectedMemberInfo,
    setUserConsentCheckboxValue,
    handleSubmit,
    getFileUploadInfo,
    setCurrencySymbol,
    handleOnChangeEvents,
    createFile,
    fileSubmit,
    apiResponse,
    setApiResponse,
    fileUploadInfo,
    handleBack,
    isSubmitButtonDisable,
    loggerApiCall,
    journeyId,
    setFormSubmissionPayload,
    isFormSubmission,
    handleContinue,
    filesToAttach,
    setFilesToAttach,
    setValidationErrors,
    validationErrors,
    setFocus,
    entityData,
    setEntityData,
    idsToClear,
    operationCaller,
    entityToEdit,
    setEntityId,
    s3FileInfo,
    setS3FileInfo,
    allS3FileIds,
    setAllS3FileIds,
    formPageType,
    disabledButtons,
    setDisabledButtons,
    setShowPopUp,
    showPopUp,
    setEvaluatePopUpId,
    curPageValues,
    setCurPageValues,
    formContainer,
    griffinFileInfo,
    setGriffinFileInfo,
    dispatchIdsState,
    comments,
    setComments,
    entityComments,
    entityCompositeKey,
    setEntityCompositeKey,
    objectOfFunctions,
    extractMapResponseValueByKey,
    setFileData,
    putDataToS3,
    s3EsignFileInfo,
    payloadFields,
    elementContainer,
    changePageValues,
    setChangePageValues,
    setS3EsignFileInfo,
    setIsFormSubmission,
    iformIntegrationUrls,
    summaryValidationResult,
    dataObject,
    selectedOptions,
    setSelectedOptions,
    setSelectedOptionLabels,
    selectedOptionLabels,
    saveItrackerStatusApi,
    currentPageStatus,
  } = props;

  /* eslint-disable-next-line no-underscore-dangle -- internal marker set during entity edit */
  const entityShowId = entityToEdit?.__entityId;

  // childProps are coming from JSON
  const {
    fieldType,
    fieldLabel,
    fieldValue,
    cssProperties,
    fieldIndicator,
    fieldHref,
    customTag,
    fieldValidation,
    target = '_blank',
    // imgProps,
  } = childProps;

  /**
   * enable elements based on any selection
   * @param {*} args
   */
  const isEnableElements = (args) => {
    refreshForm(args);
  };

  /**
   * Disable fields in review mode — d
   * efined in Element.jsx where formContainer.operations is available,
   * passed down via props.
   */
  const { getReviewDisabled } = props;

  /**
   * return element based on passed
   * field type
   * @param {*} fieldType
   */
  /* eslint complexity: ["error", 110] -- need to increase complexity */
  const elementSwitch = (type) => {
    switch (type) {
      case 'leMappings':
        return (
          <LazyWrap className={cssProperties?.style} height="60px">
            <LeMappings
              childProps={childProps}
              props={props}
              filledFormData={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'leReconDoc':
        return (
          <LazyWrap className={cssProperties?.style} height="60px">
            <LeReconDoc
              {...childProps}
              props={props}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              entityData={entityData}
            />
          </LazyWrap>
        );
      case 'groupCheckboxesMultiSelect':
        return (
          <LazyWrap className={cssProperties?.style} height="48px">
            <GroupCheckboxesMultiSelect
              {...childProps}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formData={formSubmissionPayload}
              memberInfo={selectedMemberInfo}
              handleOnChangeEvents={handleOnChangeEvents}
            />
          </LazyWrap>
        );
      case 'qrWrapper':
        return (
          <LazyWrap className={cssProperties?.style} height="200px">
            <QrWrapper
              {...childProps}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              memberInfo={selectedMemberInfo}
              handleOnChangeEvents={handleOnChangeEvents}
            />
          </LazyWrap>
        );
      case 'qrcode':
        return (<LazyWrap className={cssProperties?.style} height="200px"><QrCode {...formSubmissionPayload} /></LazyWrap>);
      case 'heading':
        return fieldIndicator ? (
          <HeadingComponent
            {...childProps}
            setFormSubmissionPayload={setFormSubmissionPayload}
            formSubmissionPayload={formSubmissionPayload}
          />
        ) : null;
      case 'a':
        return fieldIndicator ? (
          <a
            className={cssProperties.style}
            href={fieldHref}
            target={target}
            rel="noreferrer"
          >
            {parseHtml(fieldValue)}
          </a>
        ) : null;
      case 'p':
        return fieldIndicator ? (
          <p className={cssProperties.style}>
            {parseHtml(fieldValue)}
            {fieldValidation && fieldValidation.isToolTip && (
              <>
                <span> </span>
                <Tooltip id="ToolTip">
                  <TooltipTrigger>
                    <InfoTooltipButton screenReaderLabel="info" />
                  </TooltipTrigger>
                  <TooltipContent className="col-md-6">
                    {parseHtml(fieldValidation.toolTipInfo)}
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </p>
        ) : null;
      case 'pwithdata':
        return <P {...childProps} filledFormData={formSubmissionPayload} />;
      case 'IconWarning':
        return fieldIndicator ? (
          <div className={cssProperties.style}>
            <IconWarning
              filled={cssProperties.filled ? cssProperties.filled : ''}
              title={fieldValue}
              titleId={fieldValue}
              size={cssProperties.size}
              className={cssProperties.iconClass}
              color={cssProperties.iconClass === 'dls-red' ? 'critical' : 'brand'}
            />
          </div>
        ) : null;
      case 'IconSuccess':
        return fieldIndicator ? (
          <div className={cssProperties.style}>
            <IconSuccess
              title={fieldValue}
              titleId={fieldValue}
              size={cssProperties.size}
              className={cssProperties.iconClass}
              color={cssProperties.iconClass === 'dls-green' || 'dls-color-success' ? 'success' : 'brand'}
            />
          </div>
        ) : null;
      case 'IconLocation':
        return (
          fieldIndicator
            ? (
              <div
                className={cssProperties.style}
              >
                <IconLocation
                  filled={cssProperties.filled ? cssProperties.filled : ''}
                  title={fieldValue}
                  titleId={fieldValue}
                  size={cssProperties.size}
                  className={cssProperties.iconClass}
                  color={cssProperties.iconClass === 'dls-red' ? 'critical' : 'brand'}
                />
              </div>
            ) : null
        );
      case 'IconInfo':
        return (
          fieldIndicator
            ? (
              <div
                className={cssProperties.style}
              >
                <IconInfo
                  filled={cssProperties.filled ? cssProperties.filled : ''}
                  title={fieldValue}
                  titleId={fieldValue}
                  size={cssProperties.size}
                  className={cssProperties.iconClass === 'dls-red' ? 'critical' : 'brand'}
                />
              </div>
            ) : null
        );
      case 'camera':
        return (
          <LazyWrap className={cssProperties?.style} height="200px">
            <div className={cssProperties.style}>
              <Camera />
            </div>
          </LazyWrap>
        );
      case 'cameraVideo':
        return (
          <LazyWrap className={cssProperties?.style} height="200px">
            <div className={cssProperties.style}>
              <CameraVideo createFile={createFile} />
            </div>
          </LazyWrap>
        );
      case 'img':
        return (
          fieldIndicator ? (
            <LazyWrap className={cssProperties?.style} height="100px">
              <Image {...childProps} memberInfo={selectedMemberInfo} />
            </LazyWrap>
          ) : null
        );
      case 'groupedImg':
        return (
          fieldIndicator ? (
            <LazyWrap className={cssProperties?.style} height="150px">
              <GroupedImageSelect
                {...childProps}
                memberInfo={selectedMemberInfo}
                setFormSubmissionPayload={setFormSubmissionPayload}
                formSubmissionPayload={formSubmissionPayload}
              />
            </LazyWrap>
          ) : null
        );
      case 'file':
      case 'number':
      case 'email':
      case 'text':
        return (
          <div className={getClassNameWithoutHorizontalPadding(cssProperties.style)}>
            <Input
              {...childProps}
              filledFormData={formSubmissionPayload}
              memberInfo={selectedMemberInfo}
              fileSubmit={fileSubmit}
              handleOnChangeEvents={handleOnChangeEvents}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
            />
          </div>
        );
      case 'dlsInput':
        return (
          <div className={getClassNameWithoutHorizontalPadding(cssProperties.style)}>
            <DlsInput
              {...childProps}
              entityCompositeKey={entityCompositeKey}
              showId={entityShowId}
              loggerApiCall={loggerApiCall}
              entityComments={entityComments}
              comments={comments}
              setComments={setComments}
              idsToClear={idsToClear}
              filledFormData={formSubmissionPayload}
              memberInfo={selectedMemberInfo}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
              handleOnChangeEvents={handleOnChangeEvents}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
              entityToEdit={entityToEdit}
              curPageValues={curPageValues}
              setCurPageValues={setCurPageValues}
              dispatchIdsState={dispatchIdsState}
              extractMapResponseValueByKey={extractMapResponseValueByKey}
              dataObject={{
                memberInfo: selectedMemberInfo,
                formSubmissionPayload,
                curPageValues,
                formContainer,
                entityData,
              }}
              changePageValues={changePageValues}
              setChangePageValues={setChangePageValues}
              journeyId={journeyId}
              iformIntegrationUrls={iformIntegrationUrls}
              getReviewDisabled={getReviewDisabled}
              objectOfFunctions={objectOfFunctions}
              setIsFormSubmission={setIsFormSubmission}
            />
          </div>
        );
      case 'dlsCarousel':
        return (
          <LazyWrap className={cssProperties?.style} height="200px">
            <div
              className={cssProperties.style}
            >
              <DlsCarousel
                {...childProps}
                props={props}
              />
            </div>
          </LazyWrap>
        );
      case 'splitInput':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <div
              className={cssProperties.style}
            >
              <SplitInput
                {...childProps}
                formSubmissionPayload={formSubmissionPayload}
                setValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
                setFocus={setFocus}
              />
            </div>
          </LazyWrap>
        );
      case 'copyLink':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <div
              className={cssProperties.style}
            >
              <CopyLink
                {...childProps}
                formSubmissionPayload={formSubmissionPayload}
              />
            </div>
          </LazyWrap>
        );
      case 'createQr':
        return (
          <LazyWrap className={cssProperties?.style} height="200px">
            <div
              className={cssProperties.style}
            >
              <CreateQr
                {...childProps}
                formSubmissionPayload={formSubmissionPayload}
              />
            </div>
          </LazyWrap>
        );
      case 'textArea':
        return (
          <div className={getClassNameWithoutHorizontalPadding(cssProperties.style)}>
            <TextArea
              {...childProps}
              filledFormData={formSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
              memberInfo={selectedMemberInfo}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
              curPageValues={curPageValues}
              setCurPageValues={setCurPageValues}
              comments={comments}
              setComments={setComments}
              entityCompositeKey={entityCompositeKey}
              showId={entityShowId}
              entityComments={entityComments}
              idsToClear={idsToClear}
              entityToEdit={entityToEdit}
              objectOfFunctions={objectOfFunctions}
              getReviewDisabled={getReviewDisabled}
            />
          </div>
        );
      case 'password':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <div
              className={getClassNameWithoutHorizontalPadding(cssProperties.style)}
            >
              <Password
                {...childProps}
                setValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
                setFocus={setFocus}
                formSubmissionPayload={formSubmissionPayload}
                setFormSubmissionPayload={setFormSubmissionPayload}
                operationCaller={operationCaller}
                dataObject={{
                  memberInfo: selectedMemberInfo,
                  formSubmissionPayload,
                  curPageValues,
                  formContainer,
                  entityData,
                }}
              />
            </div>
          </LazyWrap>

        );
      case 'select':
        return (
          <Select
            {...childProps}
            handleOnChangeEvents={handleOnChangeEvents}
            filledFormData={formSubmissionPayload}
          />
        );
      case 'dlsSelect':
        return (
          <DlsSelect
            {...childProps}
            comments={comments}
            setComments={setComments}
            loggerApiCall={loggerApiCall}
            handleOnChangeEvents={handleOnChangeEvents}
            filledFormData={formSubmissionPayload}
            setFormSubmissionPayload={setFormSubmissionPayload}
            setValidationErrors={setValidationErrors}
            validationErrors={validationErrors}
            setFocus={setFocus}
            entityToEdit={entityToEdit}
            idsToClear={idsToClear}
            setShowPopUp={setShowPopUp}
            curPageValues={curPageValues}
            setCurPageValues={setCurPageValues}
            formContainer={formContainer}
            formSubmissionPayload={formSubmissionPayload}
            memberInfo={selectedMemberInfo}
            dispatchIdsState={dispatchIdsState}
            objectOfFunctions={objectOfFunctions}
            entityData={entityData}
            changePageValues={changePageValues}
            setChangePageValues={setChangePageValues}
            dataObject={dataObject}
            setEntityData={setEntityData}
            setIsFormSubmission={setIsFormSubmission}
            entityComments={entityComments}
            entityCompositeKey={entityCompositeKey}
            showId={entityShowId}
          />
        );
      case 'multiSelect':
        return (
          <LazyWrap className={cssProperties?.style} height="48px">
            <MultiSelectComponent
              {...childProps}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
              idsToClear={idsToClear}
              dispatchIdsState={dispatchIdsState}
              entityToEdit={entityToEdit}
              changePageValues={changePageValues}
              setChangePageValues={setChangePageValues}
            />
          </LazyWrap>
        );
      case 'groupedCheckboxes':
        return (
          <LazyWrap className={cssProperties?.style} height="48px">
            <GroupedCheckboxes
              {...childProps}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              curPageValues={curPageValues}
              setCurPageValues={setCurPageValues}
              memberInfo={selectedMemberInfo}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
            />
          </LazyWrap>
        );
      case 'popUp':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <PopUp
              {...childProps}
              props={props}
              setShowPopUp={setShowPopUp}
              showPopUp={showPopUp}
            />
          </LazyWrap>
        );
      case 'checkbox':
        return (
          <Checkbox
            comments={comments}
            setComments={setComments}
            checkConsent={setUserConsentCheckboxValue}
            setFormSubmissionPayload={setFormSubmissionPayload}
            formSubmissionPayload={formSubmissionPayload}
            {...childProps}
            setSelectedOptions={setSelectedOptions}
            selectedOptions={selectedOptions}
            selectedOptionLabels={selectedOptionLabels}
            setSelectedOptionLabels={setSelectedOptionLabels}
            setShowPopUp={setShowPopUp}
            filledFormData={formSubmissionPayload}
            disabledButtons={disabledButtons}
            setDisabledButtons={setDisabledButtons}
            curPageValues={curPageValues}
            setCurPageValues={setCurPageValues}
            dispatchIdsState={dispatchIdsState}
            idsToClear={idsToClear}
            entityToEdit={entityToEdit}
            getReviewDisabled={getReviewDisabled}
            changePageValues={changePageValues}
            setChangePageValues={setChangePageValues}
            dataObject={dataObject}
          />
        );
      case 'radioButton':
        return (
          <RadioButton
            {...childProps}
            entityCompositeKey={entityCompositeKey}
            showId={entityShowId}
            entityComments={entityComments}
            entityData={entityData}
            setEntityData={setEntityData}
            entityToEdit={entityToEdit}
            comments={comments}
            setComments={setComments}
            memberInfo={selectedMemberInfo}
            filledFormData={formSubmissionPayload}
            formSubmissionPayload={formSubmissionPayload}
            setFormSubmissionPayload={setFormSubmissionPayload}
            setShowPopUp={setShowPopUp}
            setValidationErrors={setValidationErrors}
            validationErrors={validationErrors}
            setFocus={setFocus}
            idsToClear={idsToClear}
            formContainer={formContainer}
            curPageValues={curPageValues}
            setCurPageValues={setCurPageValues}
            dispatchIdsState={dispatchIdsState}
            changePageValues={changePageValues}
            setChangePageValues={setChangePageValues}
            getReviewDisabled={getReviewDisabled}
            loggerApiCall={loggerApiCall}
            journeyId={journeyId}
            dataObject={dataObject}
          />
        );
      case 'button':
        return fieldIndicator ? (
          <Button
            {...childProps}
            handleBack={handleBack}
            handleContinue={handleContinue}
            isSubmitButtonDisable={isSubmitButtonDisable}
            isFormSubmission={isFormSubmission}
            setEntityId={setEntityId}
            setFormSubmissionPayload={setFormSubmissionPayload}
            disabledButtons={disabledButtons}
            setShowPopUp={setShowPopUp}
            setEvaluatePopUpId={setEvaluatePopUpId}
            filledFormData={formSubmissionPayload}
            curPageValues={curPageValues}
            dispatchIdsState={dispatchIdsState}
            idsToClear={idsToClear}
            saveItrackerStatusApi={saveItrackerStatusApi}
            pageStatus={childProps.pageStatus || currentPageStatus}
          />
        ) : null;
      case 'callApiButton':
        return (
          fieldIndicator ? (
            <LazyWrap className={cssProperties?.style} height="44px">
              <ApiButton
                {...childProps}
                filledFormData={formSubmissionPayload}
                setFormSubmissionPayload={setFormSubmissionPayload}
                loggerApiCall={loggerApiCall}
                handleContinue={handleContinue}
                curPageValues={curPageValues}
              />
            </LazyWrap>
          ) : null
        );
      case 'currencyInput':
        return fieldIndicator ? (
          <LazyWrap className={cssProperties?.style} height="40px">
            <div className={getClassNameWithoutHorizontalPadding(cssProperties.style)}>
              <Currency
                {...childProps}
                handleOnChangeEvents={handleOnChangeEvents}
                filledFormData={formSubmissionPayload}
                setCurrencySymbol={setCurrencySymbol}
                setValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
                setFocus={setFocus}
                extractMapResponseValueByKey={extractMapResponseValueByKey}
                getReviewDisabled={getReviewDisabled}
              />
            </div>
          </LazyWrap>
        ) : null;
      case 'list':
        return (
          fieldIndicator ? (
            <LazyWrap className={cssProperties?.style} height="60px">
              <List
                {...childProps}
                dataObject={dataObject}
              />
            </LazyWrap>
          ) : null
        );
      case 'accordion':
        return (
          <LazyWrap className={cssProperties?.style} height="60px">
            <DlsAccordion
              {...childProps}
              className={cssProperties.style}
              handleOnChangeEvents={handleOnChangeEvents}
              memberInfo={selectedMemberInfo}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              isEnableElements={isEnableElements}
            />
          </LazyWrap>
        );
      case 'dynamicDlsAccordion':
        return (
          <LazyWrap className={cssProperties?.style} height="60px">
            <DynamicDlsAccordion
              {...childProps}
              props={props}
              className={cssProperties.style}
              handleOnChangeEvents={handleOnChangeEvents}
              memberInfo={selectedMemberInfo}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              isEnableElements={isEnableElements}
            />
          </LazyWrap>
        );
      case 'dlsTab':
      case 'tabs':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <DlsTab
              {...childProps}
              props={props}
              className={cssProperties.style}
              handleOnChangeEvents={handleOnChangeEvents}
              memberInfo={selectedMemberInfo}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              isEnableElements={isEnableElements}
            />
          </LazyWrap>
        );
      case 'actionableCard':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <ActionableCard
              {...childProps}
              props={props}
            />
          </LazyWrap>
        );
      case 'table':
        return (
          <div className={cssProperties.style}>
            <table className={`table ${fieldLabel.cssProperties.style}`}>
              <thead>
                <tr>
                  {fieldLabel.tableData.map((cell) => (
                    <>
                      {cell.th.map((value) => (
                        <th
                          key={value}
                          className="text-align-center"
                          style={{ textTransform: 'capitalize' }}
                        >
                          {value}
                        </th>
                      ))}
                    </>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fieldLabel.tableData.map(
                  (cell) => cell.tr
                    && cell.tr.map((value) => <tr key={value}>{value}</tr>)
                )}
              </tbody>
            </table>
          </div>
        );
      case 'fileUpload':
        return (
          <LazyWrap className={cssProperties?.style} height="120px">
            <FileUpload
              loggerApiCall={loggerApiCall}
              getFileUploadInfo={getFileUploadInfo}
              objectOfFunctions={objectOfFunctions}
              formSubmissionPayload={formSubmissionPayload}
              journeyId={journeyId}
              filledFormData={formSubmissionPayload}
              griffinFileInfo={griffinFileInfo}
              setGriffinFileInfo={setGriffinFileInfo}
              formPageType={formPageType}
              disabledButtons={disabledButtons}
              setDisabledButtons={setDisabledButtons}
              {...childProps}
            />
          </LazyWrap>
        );
      case 'attachFilesToPayload':
        return (
          <LazyWrap className={cssProperties?.style} height="120px">
            <PayloadFileAttachment
              loggerApiCall={loggerApiCall}
              entityToEdit={entityToEdit}
              journeyId={journeyId}
              filledFormData={formSubmissionPayload}
              filesToAttach={filesToAttach}
              setFilesToAttach={setFilesToAttach}
              s3FileInfo={s3FileInfo}
              setS3FileInfo={setS3FileInfo}
              formPageType={formPageType}
              allS3FileIds={allS3FileIds}
              setAllS3FileIds={setAllS3FileIds}
              disabledButtons={disabledButtons}
              setDisabledButtons={setDisabledButtons}
              {...childProps}
            />
          </LazyWrap>
        );

      case 'attachFilesToPayloadV2':
        return (
          <LazyWrap className={cssProperties?.style} height="120px">
            <PayloadFileAttachmentV2
              loggerApiCall={loggerApiCall}
              entityToEdit={entityToEdit}
              journeyId={journeyId}
              filledFormData={formSubmissionPayload}
              filesToAttach={filesToAttach}
              setFilesToAttach={setFilesToAttach}
              s3FileInfo={s3FileInfo}
              setS3FileInfo={setS3FileInfo}
              formPageType={formPageType}
              allS3FileIds={allS3FileIds}
              setAllS3FileIds={setAllS3FileIds}
              disabledButtons={disabledButtons}
              setDisabledButtons={setDisabledButtons}
              comments={comments}
              setComments={setComments}
              entityData={entityData}
              setShowPopUp={setShowPopUp}
              setFormSubmissionPayload={setFormSubmissionPayload}
              {...childProps}

            />

          </LazyWrap>
        );
      case 'modal':
        return (<LazyWrap className={cssProperties?.style} height="100px"><Modal {...childProps} /></LazyWrap>);
      case 'hr':
        return <hr className={cssProperties.style} />;
      case 'badge':
        return <span className="badge badge-outline filled">All</span>;
      case 'hrWithWord':
        return (
          <div
            className={cssProperties.style}
            style={{
              width: '100%',
              height: '12px',
              borderBottom: '1px solid black',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                fontSize: '22px',
                backgroundColor: '#FFF',
                padding: '0 10px',
              }}
            >
              OR
            </span>
          </div>
        );
      case 'datePicker':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <DateSelector
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              idsToClear={idsToClear}
              entityToEdit={entityToEdit}
              curPageValues={curPageValues}
              setCurPageValues={setCurPageValues}
              changePageValues={changePageValues}
              setChangePageValues={setChangePageValues}
              dispatchIdsState={dispatchIdsState}
              entityComments={entityComments}
              entityCompositeKey={entityCompositeKey}
              showId={entityShowId}
              dataObject={{
                memberInfo: selectedMemberInfo,
                formSubmissionPayload,
                curPageValues,
                formContainer,
              }}
              {...childProps}
            />
          </LazyWrap>
        );
      case 'dateRangePicker':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <DlsDateRangePicker
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              {...childProps}
            />
          </LazyWrap>
        );
      case 'dateInput':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <InputDate
              {...childProps}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              setFocus={setFocus}
              filledFormData={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              idsToClear={idsToClear}
              comments={comments}
              setComments={setComments}
              entityToEdit={entityToEdit}
              entityCompositeKey={entityCompositeKey}
              showId={entityShowId}
              entityComments={entityComments}
              getReviewDisabled={getReviewDisabled}
              curPageValues={curPageValues}
              setCurPageValues={setCurPageValues}
            />
          </LazyWrap>
        );
      case 'consent':
        return (
          <LazyWrap className={cssProperties?.style} height="60px">
            <Consent
              {...childProps}
              handleSubmit={handleSubmit}
              fileUploadInfo={fileUploadInfo}
              isFormSubmission={isFormSubmission}
              formSubmissionPayload={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'requestLimit':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <RequestLimit
              {...childProps}
              setCurrencySymbol={setCurrencySymbol}
            />
          </LazyWrap>
        );
      case 'multitracker':
        return (
          <LazyWrap className={cssProperties?.style} height="60px">
            <MultiTracker {...childProps} />
          </LazyWrap>
        );
      case 'verticalmultitracker':
        return (
          <VerticalMultiTracker
            {...childProps}
            formSubmissionPayload={formSubmissionPayload}
            setFormSubmissionPayload={setFormSubmissionPayload}
          />
        );
      case 'banner':
        return (
          <LazyWrap className={cssProperties?.style} height="200px">
            <HeroBanner {...childProps} />
          </LazyWrap>
        );
      case 'customTag':
        switch (customTag.tagName) {
          case 'eSign': {
            return (
              <LazyWrap className={cssProperties?.style} height="200px">
                <Esign
                  elementContainer={elementContainer}
                  formSubmissionPayload={formSubmissionPayload}
                  selectedMemberInfo={selectedMemberInfo}
                  childProps={childProps}
                  formContainer={formContainer}
                  loggerApiCall={loggerApiCall}
                  handleContinue={handleContinue}
                  putDataToS3={putDataToS3}
                  s3EsignFileInfo={s3EsignFileInfo}
                  setFormSubmissionPayload={setFormSubmissionPayload}
                  journeyId={journeyId}
                  payloadFields={payloadFields}
                  setFileData={setFileData}
                  objectOfFunctions={objectOfFunctions}
                  apiResponse={apiResponse}
                  setApiResponse={setApiResponse}
                  setS3FileInfo={setS3FileInfo}
                  s3FileInfo={s3FileInfo}
                  setAllS3FileIds={setAllS3FileIds}
                  setS3EsignFileInfo={setS3EsignFileInfo}
                  setIsFormSubmission={setIsFormSubmission}
                  saveItrackerStatusApi={saveItrackerStatusApi}
                />
              </LazyWrap>
            );
          }
          case 'singpass': {
            return (
              <LazyWrap className={cssProperties?.style} height="100px">
                <Singpass
                  childProps={childProps}
                  formSubmissionPayload={formSubmissionPayload}
                  setFormSubmissionPayload={setFormSubmissionPayload}
                  journeyId={journeyId}
                  handleContinue={handleContinue}
                />
              </LazyWrap>
            );
          }
          default:
            return null;
        }
      case 'cardSelection':
        return <LazyWrap className={cssProperties?.style} height="100px"><CardSelection {...childProps} handleOnChangeEvents={handleOnChangeEvents} /></LazyWrap>;
      case 'cardPayment':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <CardPayment
              filledFormData={formSubmissionPayload}
              handleOnChangeEvents={handleOnChangeEvents}
              {...childProps}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'iconWithLabel':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <IconWithLabel
              {...childProps}
              filledFormData={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'statictable':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <StaticTable
              filledFormData={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              memberInfo={selectedMemberInfo}
              entityData={entityData}
              objectOfFunctions={objectOfFunctions}
              {...childProps}
            />
          </LazyWrap>
        );
      case 'expandableTable':
        return (
          <LazyWrap className={cssProperties?.style} height="150px">
            <ExpandableTable
              comments={comments}
              setComments={setComments}
              entityComments={entityComments}
              setEntityCompositeKey={setEntityCompositeKey}
              entityToEdit={entityToEdit}
              formSubmissionPayload={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              memberInfo={selectedMemberInfo}
              entityData={entityData}
              operationCaller={operationCaller}
              extractMapResponseValueByKey={extractMapResponseValueByKey}
              setShowPopUp={setShowPopUp}
              {...childProps}
            />
          </LazyWrap>
        );
      case 'cardDetails':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <CardDetails
              {...childProps}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}

            />
          </LazyWrap>
        );
      case 'showSubKeys':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <ShowSubKeys {...childProps} filledFormData={formSubmissionPayload} />
          </LazyWrap>
        );
      case 'reCaptcha':
        return (
          <LazyWrap className={cssProperties?.style} height="78px">
            <ReCaptcha {...childProps} />
          </LazyWrap>
        );
      case 'search':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <Search
              {...childProps}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'mapsdisplay':
        return (
          <LazyWrap className={cssProperties?.style} height="300px">
            <Maps
              {...childProps}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'anchorwithpopup':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <Anchor
              {...childProps}
              setShowPopUp={setShowPopUp}
            />
          </LazyWrap>

        );
      case 'phoneNumber':
        return (
          <LazyWrap className={cssProperties?.style} height="40px">
            <Phone
              {...childProps}
              setValidationErrors={setValidationErrors}
              validationErrors={validationErrors}
              formSubmissionPayload={formSubmissionPayload}
              entityToEdit={entityToEdit}
            />
          </LazyWrap>

        );
      case 'showsupp':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <SuppCardComponent
              {...childProps}
              memberInfo={selectedMemberInfo}
              filledFormData={formSubmissionPayload}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
              handleContinue={handleContinue}
              curPageValues={curPageValues}
              disabledButtons={disabledButtons}
              setFocus={setFocus}
              setDisabledButtons={setDisabledButtons}
            />
          </LazyWrap>

        );
      case 'showsuppDetails':
        return (
          <LazyWrap className={cssProperties?.style} height="100px">
            <SuppCardDetails
              {...childProps}
              setFormSubmissionPayload={setFormSubmissionPayload}
              formSubmissionPayload={formSubmissionPayload}
            />
          </LazyWrap>
        );
      case 'continuousLinearTracker':
        return (
          <ContinuousLinearTrackerProgress
            {...childProps}
            formSubmissionPayload={formSubmissionPayload}
          />
        );
      case 'progressCircularDls':
        return (
          <ProgressCircularDls
            {...childProps}
            formSubmissionPayload={formSubmissionPayload}
          />
        );
      case 'dlsNotification':
        return (
          <DlsNotification
            {...childProps}
            formSubmissionPayload={formSubmissionPayload}
          />
        );
      case 'nav':
        return (
          <Navigation
            {...childProps}
            formSubmissionPayload={formSubmissionPayload}
            setFormSubmissionPayload={setFormSubmissionPayload}
            elementContainer={elementContainer}
            handleContinue={handleContinue}
          />
        );
      case 'IconDeclined':
        return (
          fieldIndicator
            ? (
              <div
                className={cssProperties.style}
              >
                <IconDeclined
                  title={fieldValue}
                  titleId={fieldValue}
                  size={cssProperties.size}
                  className={cssProperties.iconClass}
                  color={cssProperties.iconClass === 'dls-red' ? 'critical' : 'brand'}
                />
              </div>
            ) : null
        );
      case 'formSummarySection':
        return (
          <FormSummarySection
            {...childProps}
            formElements={elementContainer}
            formSubmissionPayload={formSubmissionPayload}
            entityData={entityData}
            s3FileInfo={s3FileInfo}
            journeyId={journeyId}
            formPageType={formPageType}
            iformIntegrationUrls={iformIntegrationUrls}
            summaryValidationResult={summaryValidationResult}
            onEditSection={(navSection, pageNo) => {
              setFormSubmissionPayload((prev) => ({
                ...prev,
                ...navSection !== null && navSection !== undefined
                  ? { navDefaultSection: navSection } : {},
                returnToSummaryAfterEdit: true,
              }));
              if (pageNo !== null && pageNo !== undefined && handleContinue) {
                handleContinue(pageNo);
              }
            }}
          />
        );
      default:
        return null;
    }
  };
  /* eslint-enable  import/no-cycle -- need */
  return <>{elementSwitch(fieldType)}</>;
};

export default findElement;
